import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Image } from 'antd';
import { Link } from "react-router-dom";
import CarouselImage1 from '../../media/pageNotreSolution/1_carrousel_expositions.webp';
import CarouselImage2 from '../../media/pageNotreSolution/2-carrousel_coexpo.webp';
import CarouselImage3 from '../../media/pageNotreSolution/3_carrousel_caracterisation.webp';
import CarouselImage4 from '../../media/pageNotreSolution/4_carrousel_metrologies.webp';
import CarouselImage5 from '../../media/pageNotreSolution/5_carrousel_orientation_examen.webp';
import CarouselImage6 from '../../media/pageNotreSolution/6_carrousel_duree_expo.webp';
import CarouselImage7 from '../../media/pageNotreSolution/7_carrousel_suivi.webp';
import syntheseImage from '../../media/pageNotreSolution/image_synthese.webp';
import videoPoster from '../../media/pageNotreSolution/video-demo-poster.webp';
import { ReactComponent as SecurityImage } from '../../media/pageNotreSolution/illu_sécurité.svg';
import { ReactComponent as RGPD } from '../../media/RGPD.svg';
import sal from 'sal.js';
import PdfMagazine from '../../components/PdfMagazine/PdfMagazine';
import argumentairePdf from '../../media/pageNotreSolution/RAVAIL-Argumentaire-2026.pdf';
import './NotreSolutionScreen.css';

const FEATURES = [
  {
    label: 'Expositions',
    img: CarouselImage1,
    titre: 'Suggestions intelligentes',
    texte: 'Pour chaque métier, Ravail suggère automatiquement les tâches et expositions professionnelles associées, réduisant le temps de saisie et limitant les oublis.',
  },
  {
    label: 'Co-expositions',
    img: CarouselImage2,
    titre: 'Co-expositions et antécédents',
    texte: 'Aide au recensement des co-expositions ainsi que des antécédents personnels et familiaux majorant le risque de développer une maladie associée aux expositions professionnelles.',
  },
  {
    label: 'Caractérisation',
    img: CarouselImage3,
    titre: 'Caractérisation des expositions',
    texte: "La matrice expositions-tâches exclusive de Ravail pré-remplit les tâches associées à chaque exposition, facilitant leur caractérisation.",
  },
  {
    label: 'Métrologies',
    img: CarouselImage4,
    titre: 'Métrologies et biométrologies',
    texte: "Aide à la saisie des métrologies et biométrologies pertinentes, avec redirection vers la base de données Biotox de l'INRS.",
  },
  {
    label: 'Examen clinique',
    img: CarouselImage5,
    titre: "Orientation de l'examen clinique",
    texte: "Ravail précise au médecin les signes et symptômes pouvant accompagner les maladies associées aux expositions professionnelles retenues.",
  },
  {
    label: "Durées d'exposition",
    img: CarouselImage6,
    titre: 'Calcul automatisé des durées',
    texte: "Calcul automatisé des durées d'expositions et suggestions dynamiques des intensités selon la littérature scientifique en vigueur.",
  },
  {
    label: 'Suivis',
    img: CarouselImage7,
    titre: 'Suivis recommandés',
    texte: "Conformément aux recommandations des sociétés savantes, Ravail suggère les suivis adaptés en fonction des intensités globales d'exposition retenues.",
  },
  {
    label: 'Synthèse',
    img: syntheseImage,
    imgClass: 'ns-feature-img--sm',
    titre: 'Synthèse et compte-rendu',
    texte: "Génération d'une synthèse pour la médecine de ville et d'un compte-rendu exhaustif remis au salarié, notamment pour la reconnaissance de maladie professionnelle.",
  },
];

const SECURITE_ITEMS = [
  { titre: 'Données non vendues', texte: 'Vos données ne seront jamais vendues. Le secret médical est au cœur de nos préoccupations.' },
  { titre: 'Hébergement HDS', texte: 'Hébergement en France chez un opérateur certifié aux 6 niveaux HDS (Hébergeur de Données de Santé).' },
  { titre: 'Chiffrement en transit', texte: 'Données chiffrées via HTTPS avec TLS v1.2 minimum requis pour accéder au site.' },
  { titre: 'Chiffrement au repos', texte: "Standards cryptographiques recommandés par l'ANSSI pour la protection des données stockées." },
  { titre: 'Audit cybersécurité', texte: 'Audits réguliers par un cabinet de cybersécurité externe pour prévenir toute attaque.' },
  { titre: 'Double authentification', texte: 'Le personnel médical habilité est soumis à une authentification à double facteur.' },
];

function NotreSolutionScreen() {
  const [activeFeature, setActiveFeature] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef(null);
  const handlePlayClick = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setVideoPlaying(true);
    }
  };

  useEffect(() => {
    sal();
  }, []);

  return (
    <div className="ns-page">

      {/* ── HERO + VIDÉO ── */}
      <section className="ns-hero" id="video">
        <div className="ns-inner">
          <div data-sal="slide-up" data-sal-duration="500">
            <h1 className="ns-hero-title">Notre solution</h1>
            <p className="ns-hero-intro">
              Un logiciel médical complet pour structurer l'analyse des expositions professionnelles et simplifier le suivi des salariés.
            </p>
          </div>
          <div className="ns-video-wrap" data-sal="slide-up" data-sal-duration="700" data-sal-delay="150">
            <video ref={videoRef} className="ns-video" controls preload="none" poster={videoPoster} onPlay={() => setVideoPlaying(true)}>
              <source src="/presentation-ANS.webm" type="video/webm" />
              <source src="/presentation-ANS.mp4" type="video/mp4" />
              Télécharger la vidéo au <a href="/presentation-ANS.webm">format WEBM</a> ou au <a href="/presentation-ANS.mp4">format MP4</a>.
            </video>
            {!videoPlaying && (
              <button className="ns-video-play" onClick={handlePlayClick} aria-label="Lancer la vidéo">
                <span className="ns-video-play-icon">▶</span>
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="wave-ns-to-content" aria-hidden="true">
        <svg viewBox="0 0 1280 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120 H1280 V60 C1000 0 300 120 0 40 Z" fill="#fefefe"/>
        </svg>
      </div>

      {/* ── SECTIONS MÉTIER ── */}
      <div className="ns-demo-cta">
        <Link to="/contact?tab=demo" className="ns-cta-btn ns-cta-btn--primary">Demander une démo</Link>
      </div>

      {/* ── TIMELINE FONCTIONNALITÉS ── */}
      <section className="ns-section ns-section--timeline" id="fonctionnalites">
        <div className="ns-inner">
            <h2 className="ns-section-title" data-sal="slide-up" data-sal-duration="500">Nos fonctionnalités</h2>
          <p className="ns-section-subtitle" data-sal="slide-up" data-sal-duration="500" data-sal-delay="100">Découvrez les fonctionnalités des étapes clés. En mode rapide, le parcours prend seulement quelques minutes grâce aux étapes automatiquement remplies et passées selon vos paramètres.</p>
        <div className="ns-tl-layout">
            <div className="ns-tl-sidebar">
              {FEATURES.map((f, i) => (
                <button
                  key={i}
                  className={`ns-tl-item${activeFeature === i ? ' ns-tl-item--active' : ''}`}
                  onClick={() => setActiveFeature(i)}
                  aria-label={f.titre}
                >
                  <div className="ns-tl-item-row">
                    <span className="ns-tl-dot"><span className="ns-tl-number">{i + 1}</span></span>
                    <span className="ns-tl-label">{f.label}</span>
                  </div>
                  {i < FEATURES.length - 1 && <span className="ns-tl-line" />}
                </button>
              ))}
            </div>
            <div className="ns-tl-content">
              <div className="ns-tl-content-panel" key={activeFeature}>
                <Row gutter={[48, 32]} align="middle">
                  <Col xs={24} md={12}>
                    <h3 className="ns-feature-title">{FEATURES[activeFeature].titre}</h3>
                    <p className="ns-feature-text">{FEATURES[activeFeature].texte}</p>
                  </Col>
                  <Col xs={24} md={12}>
                    <div className={FEATURES[activeFeature].imgClass ? 'ns-feature-img-wrap--sm' : ''}>
                      <Image src={FEATURES[activeFeature].img} alt={FEATURES[activeFeature].titre} className="ns-feature-img" />
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SÉCURITÉ ── */}
      <section className="ns-section ns-section--securite" id="securite">
        <div className="ns-inner">
          <Row gutter={[64, 40]} align="middle">
            <Col xs={24} lg={14}>
              <h2 className="ns-section-title" data-sal="slide-up" data-sal-duration="500">Sécurité des données</h2>
              <div className="ns-securite-grid" data-sal="slide-up" data-sal-duration="600" data-sal-delay="100">
                {SECURITE_ITEMS.map((item, i) => (
                  <div key={i} className="ns-securite-card">
                    <span className="ns-securite-card-titre">{item.titre}</span>
                    <p className="ns-securite-card-texte">{item.texte}</p>
                  </div>
                ))}
              </div>
              <div className="ns-securite-rgpd" data-sal="slide-up" data-sal-duration="500" data-sal-delay="200">
                <RGPD className="ns-rgpd-icon" />
                <span>Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, de portabilité et d'effacement de vos données.</span>
              </div>
            </Col>
            <Col xs={24} lg={10} data-sal="slide-left" data-sal-duration="700" data-sal-delay="150">
              <SecurityImage className="ns-securite-illustration" />
            </Col>
          </Row>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section className="ns-cta">
        <div className="ns-inner ns-cta-inner">
          <h2 className="ns-cta-title">Envie d'en savoir plus ?</h2>
          <p className="ns-cta-subtitle">Feuilletez notre argumentaire 2026 ou contactez-nous directement.</p>
          <PdfMagazine file={argumentairePdf} downloadUrl={argumentairePdf} />
          <div className="ns-cta-actions">
            <Link to="/contact" className="ns-cta-btn ns-cta-btn--primary">Nous contacter</Link>
            <a href="https://www.linkedin.com/company/ravail" target="_blank" rel="noopener noreferrer" className="ns-cta-btn ns-cta-btn--secondary">LinkedIn</a>
          </div>
        </div>
      </section>

    </div>
  );
}

export default NotreSolutionScreen;
